// src/pages/Level-6.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LevelTemplate from "./LevelTemplate";
import { STREAM_VIDEOS } from "../videos/streamVideos";
import { TrophyStorage } from "../services/trophyStorage";

/* ─────────────────────────────────────────────
   CONFIG – Level 6 (V1.1) — Spider Dance
   - Scene video first, then melody page
   - Cloudflare Stream
   - Uses your Spider hero image
   ───────────────────────────────────────────── */

// Cloudflare Stream Video ID (from your screenshot)
const LEVEL6_FALLBACK_STREAM_ID = "fe0b608ee5d194e1c728f26821db230d";

/*
  ✅ If you ALSO add this into src/videos/streamVideos.js later, you can use it as single source of truth:
  STREAM_VIDEOS.L6_SPIDER_INTRO = { id: "fe0b608ee5d194e1c728f26821db230d", title: "L6 Spider Intro" }
*/
const LEVEL6_STREAM_ID =
  STREAM_VIDEOS?.L6_SPIDER_INTRO?.id || LEVEL6_FALLBACK_STREAM_ID;

// ✅ LevelTemplate supports: videoSrc="stream:<id>"
const LEVEL6_VIDEO_SRC = `stream:${LEVEL6_STREAM_ID}`;

// ✅ Hero image (place file in: public/images/Spider-Hero.png)
const LEVEL6_HERO_IMAGE = "/images/" + encodeURIComponent("Spider-Hero.png");

/* ─────────────────────────────────────────────
   MELODY — Incy Wincy Spider
   Notes + lyric syllable groupings matched to your sheet.
   ───────────────────────────────────────────── */

const LEVEL6_MELODY_LINES = [
  {
    id: 0,
    notes: ["C", "C", "C", "D", "E", "E"],
    lyric: "Incy wincy spi-der",
  },
  {
    id: 1,
    notes: ["E", "D", "C", "D", "E", "C"],
    lyric: "Climbed up the wat-er spout",
  },
  {
    id: 2,
    notes: ["E", "E", "F", "G"],
    lyric: "Down came the rain,",
  },
  {
    id: 3,
    notes: ["G", "F", "E", "F", "G", "E"],
    lyric: "And washed the spi-der out",
  },
  {
    id: 4,
    notes: ["C", "C", "D", "E", "E"],
    lyric: "Out came the sun and",
  },
  {
    id: 5,
    notes: ["D", "C", "D", "E", "C", "C"],
    lyric: "Dried up all the rain, and",
  },
  {
    id: 6,
    notes: ["C", "C", "C", "D", "E", "E"],
    lyric: "Incy wincy spi-der",
  },
  {
    id: 7,
    notes: ["E", "D", "C", "D", "E", "C"],
    lyric: "Climbed up the spout a-gain",
  },
];

// ✅ Syllable/word group labels per line (drives your grouped lyric UI)
const LEVEL6_LYRIC_GROUPS = [
  // Line 1: C C C D E E
  ["Incy", "wincy", "spi-", "der"],

  // Line 2: E D C D E C
  ["Climbed", "up", "the", "wat-", "er", "spout"],

  // Line 3: E E F G
  ["Down", "came", "the", "rain,"],

  // Line 4: G F E F G E
  ["And", "washed", "the", "spi-", "der", "out"],

  // Line 5: C C D E E
  ["Out", "came", "the", "sun", "and"],

  // Line 6: D C D E C C
  ["Dried", "up", "all", "the", "rain,", "and"],

  // Line 7: repeat
  ["Incy", "wincy", "spi-", "der"],

  // Line 8: E D C D E C
  ["Climbed", "up", "the", "spout", "a-", "gain"],
];

// ✅ Note-index group ranges per line (must match the labels above)
const LEVEL6_NOTE_GROUP_INDEXES = [
  // Line 1: ["C","C","C","D","E","E"]
  [
    { start: 0, end: 1 }, // Incy
    { start: 2, end: 3 }, // wincy
    { start: 4, end: 4 }, // spi-
    { start: 5, end: 5 }, // der
  ],

  // Line 2: ["E","D","C","D","E","C"] (each word gets one note)
  [
    { start: 0, end: 0 }, // Climbed
    { start: 1, end: 1 }, // up
    { start: 2, end: 2 }, // the
    { start: 3, end: 3 }, // wat-
    { start: 4, end: 4 }, // er
    { start: 5, end: 5 }, // spout
  ],

  // Line 3: ["E","E","F","G"]
  [
    { start: 0, end: 0 }, // Down
    { start: 1, end: 1 }, // came
    { start: 2, end: 2 }, // the
    { start: 3, end: 3 }, // rain
  ],

  // Line 4: ["G","F","E","F","G","E"]
  [
    { start: 0, end: 0 }, // And
    { start: 1, end: 1 }, // washed
    { start: 2, end: 2 }, // the
    { start: 3, end: 3 }, // spi-
    { start: 4, end: 4 }, // der
    { start: 5, end: 5 }, // out
  ],

  // Line 5: ["C","C","D","E","E"]
  [
    { start: 0, end: 0 }, // Out
    { start: 1, end: 1 }, // came
    { start: 2, end: 2 }, // the
    { start: 3, end: 3 }, // sun
    { start: 4, end: 4 }, // and
  ],

  // Line 6: ["D","C","D","E","C","C"]
  [
    { start: 0, end: 0 }, // Dried
    { start: 1, end: 1 }, // up
    { start: 2, end: 2 }, // all
    { start: 3, end: 3 }, // the
    { start: 4, end: 4 }, // rain
    { start: 5, end: 5 }, // and
  ],

  // Line 7: repeat (same as line 1)
  [
    { start: 0, end: 1 }, // Incy
    { start: 2, end: 3 }, // wincy
    { start: 4, end: 4 }, // spi-
    { start: 5, end: 5 }, // der
  ],

  // Line 8: ["E","D","C","D","E","C"]
  [
    { start: 0, end: 0 }, // Climbed
    { start: 1, end: 1 }, // up
    { start: 2, end: 2 }, // the
    { start: 3, end: 3 }, // spout
    { start: 4, end: 4 }, // a-
    { start: 5, end: 5 }, // gain
  ],
];

/* ─────────────────────────────────────────────
   HERO (NO overlay text)
   + Emoji burst:
     - correct: 🕷️
     - wrong: 💧
   Crop bias: keep spider face centered
   ───────────────────────────────────────────── */

function Level6Hero({ backgroundImage, noteEvent }) {
  const [bursts, setBursts] = useState([]);
  const idRef = useRef(1);
  const lastEventRef = useRef(null);

  const safeRand = useMemo(() => {
    const seed = Math.random() * 9999;
    return () => {
      const x = Math.sin(seed + idRef.current * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    };
  }, []);

  useEffect(() => {
    if (!noteEvent) return;
    if (noteEvent === lastEventRef.current) return;

    lastEventRef.current = noteEvent;

    const isHit = noteEvent === "hit";
    const emoji = isHit ? "🕷️" : "💧";

    const id = idRef.current++;
    const left = 18 + safeRand() * 64;
    const top = 18 + safeRand() * 52;

    const burst = { id, emoji, left, top, isHit };
    setBursts((prev) => [...prev, burst]);

    const t = setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== id));
    }, 750);

    return () => clearTimeout(t);
  }, [noteEvent, safeRand]);

  return (
    <div className="relative w-full max-w-3xl mx-auto rounded-[22px] sm:rounded-[24px] shadow bg-slate-900/5 overflow-hidden">
      {backgroundImage && (
        <img
          src={backgroundImage}
          alt=""
          className="w-full h-40 sm:h-56 object-cover"
          style={{ objectPosition: "50% 45%" }}
          draggable={false}
        />
      )}

      {bursts.map((b) => (
        <div
          key={b.id}
          className="tt-burst"
          style={{ left: `${b.left}%`, top: `${b.top}%` }}
          aria-hidden="true"
        >
          <span
            className={
              "tt-burst-emoji " + (b.isHit ? "tt-burst-hit" : "tt-burst-miss")
            }
          >
            {b.emoji}
          </span>
          {b.isHit && <span className="tt-burst-zzz">Web!</span>}
        </div>
      ))}

      <style>{`
        .tt-burst{
          position:absolute;
          transform: translate(-50%, -50%);
          pointer-events:none;
          z-index: 5;
        }
        .tt-burst-emoji{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          font-size: 34px;
          filter: drop-shadow(0 6px 10px rgba(0,0,0,0.25));
          animation: ttFloatUp 750ms cubic-bezier(.2,.9,.2,1) forwards;
          will-change: transform, opacity;
        }
        .tt-burst-zzz{
          display:inline-block;
          margin-left: 6px;
          font-weight: 900;
          font-size: 18px;
          color: rgba(255,255,255,0.95);
          text-shadow: 0 6px 12px rgba(0,0,0,0.35);
          animation: ttFloatUp 750ms cubic-bezier(.2,.9,.2,1) forwards;
        }
        @keyframes ttFloatUp{
          0%   { transform: translateY(0px) scale(1); opacity: 1; }
          70%  { transform: translateY(-26px) scale(1.18); opacity: 1; }
          100% { transform: translateY(-46px) scale(1.05); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function Level6() {
  const navigate = useNavigate();

  return (
    <LevelTemplate
      levelId={6}
      currentLevelForSidebar={6}
      videoSrc={LEVEL6_VIDEO_SRC}
      startLabel="Start Game"
      melodyLines={LEVEL6_MELODY_LINES}
      lyricGroupsPerLine={LEVEL6_LYRIC_GROUPS}
      noteGroupIndexesPerLine={LEVEL6_NOTE_GROUP_INDEXES}
      backgroundImage={LEVEL6_HERO_IMAGE}
      heroImageFit="cover"
      renderHero={(heroProps) => (
        <Level6Hero
          backgroundImage={heroProps.backgroundImage}
          noteEvent={heroProps.noteEvent}
        />
      )}
      completionTitle="Spider Star unlocked!"
      completionBody={
        <>
          You played{" "}
          <span className="font-semibold text-[#4F46E5]">Incy Wincy Spider</span>{" "}
          perfectly 🕷️🎶 The cave webs sparkle — grab your{" "}
          <span className="font-semibold text-[#4F46E5]">Spider Star</span> and
          keep going!
        </>
      }
      // ✅ SUCCESS TRIGGER (Level-6 completed + star "L6" collected)
      onComplete={() => {
        TrophyStorage.markLevelAndStar(6); // marks level "6" + star "L6"
      }}
      onNextLevel={() => navigate("/level-7")}
    />
  );
}
